import { useEffect } from 'react';
import type { RefObject } from 'react';
import { applyNodeChanges, getDraggedPositions, getDragItems, getNodeClickSelection, isNodeSelectable, pointToFlow } from '../core';
import type { DragItem, Node, NodeChange, XYPosition } from '../core';
import { useStoreApi } from '../store';
import type { MindmapStoreApi, NodeDragHandler } from '../store';
import { isInsideClass, isMultiSelectEvent, NO_DRAG_CLASS, RENDERER_SELECTOR, rendererPoint, suppressNextClick } from '../utils';
import { startAutoPan } from './autoPan';
import type { AutoPan } from './autoPan';

const PRIMARY_BUTTON = 0;

type Moved = { id: string; position: XYPosition };
type DragState = {
  pointerId: number;
  renderer: Element;
  start: XYPosition;
  last: XYPosition;
  items: DragItem[];
  moved: Moved[];
  hasMoved: boolean;
  autoPan: AutoPan<PointerEvent> | null;
};

const toFlowPoint = (renderer: Element, event: PointerEvent, store: MindmapStoreApi): XYPosition =>
  pointToFlow(rendererPoint(renderer, event), store.getState().viewport);

/** v11 handler arguments: the grabbed node and all dragged nodes, at their latest positions. */
const eventNodes = (store: MindmapStoreApi, moved: readonly Moved[]): Node[] => {
  const { nodeLookup } = store.getState();
  return moved.flatMap(({ id, position }) => {
    const node = nodeLookup.get(id)?.node;
    return node ? [{ ...node, position, positionAbsolute: position }] : [];
  });
};

/**
 * Selects the grabbed node like a click (v11 `selectNodesOnDrag`) and returns the nodes as selected after that click.
 * In controlled mode the store only sees the new selection once the app re-renders, so it is applied here directly.
 */
const selectOnDragStart = (store: MindmapStoreApi, id: string, event: PointerEvent): Node[] => {
  const { nodes, edges, nodeLookup, applySelection } = store.getState();
  const node = nodeLookup.get(id)?.node;
  if (!node || !isNodeSelectable(node)) return nodes;
  const selection = getNodeClickSelection(nodes, edges, id, isMultiSelectEvent(event));
  applySelection(selection);
  return applyNodeChanges(selection.nodeChanges, nodes);
};

const notify = (handler: NodeDragHandler | undefined, event: PointerEvent, grabbedId: string, nodes: Node[]) => {
  const grabbed = nodes.find((node) => node.id === grabbedId);
  if (handler && grabbed) handler(event, grabbed, nodes);
};

/**
 * Drags a node with the primary button. The threshold is 0, so a plain click is a drag too and ends with
 * `onNodeDragStop` — the app relies on that (01-audit.md). `.nodrag` opts out. The node is selected when the drag
 * starts, so an unselected node moves alone, and a node that is part of a multi-selection moves the whole selection
 * (measured on v11, D-030).
 */
export const useNodeDrag = (nodeRef: RefObject<HTMLElement>, id: string, isDraggable: boolean): void => {
  const store = useStoreApi();
  useEffect(() => {
    const element = nodeRef.current;
    if (!element || !isDraggable) return undefined;
    let drag: DragState | null = null;

    const handlePointerDown = (event: PointerEvent) => {
      if (event.button !== PRIMARY_BUTTON || isInsideClass(event.target, NO_DRAG_CLASS)) return;
      const renderer = element.closest(RENDERER_SELECTOR);
      const nodesAfterClick = selectOnDragStart(store, id, event);
      const { nodesDraggable, dragHandlers } = store.getState();
      const items = getDragItems(nodesAfterClick, id, nodesDraggable);
      if (!renderer || items.length === 0) return;
      const start = toFlowPoint(renderer, event, store);
      const moved = items.map((item) => ({ id: item.id, position: item.startPosition }));
      drag = { pointerId: event.pointerId, renderer, start, last: start, items, moved, hasMoved: false, autoPan: null };
      element.setPointerCapture(event.pointerId);
      notify(dragHandlers.onNodeDragStart, event, id, eventNodes(store, moved));
    };

    // Also runs after every auto-pan step with the last pointer event: the node follows the pointer across the canvas.
    const moveTo = (event: PointerEvent) => {
      if (!drag) return;
      const point = toFlowPoint(drag.renderer, event, store);
      // v11 only reports a drag step when the pointer really moved; a click must not write `positionAbsolute`.
      if (point.x === drag.last.x && point.y === drag.last.y) return;
      drag.last = point;
      drag.hasMoved = true;
      drag.moved = getDraggedPositions(drag.items, { x: point.x - drag.start.x, y: point.y - drag.start.y });
      const changes: NodeChange[] = drag.moved.map(({ id: movedId, position }) => ({
        id: movedId, type: 'position', position, positionAbsolute: position, dragging: true,
      }));
      store.getState().triggerNodeChanges(changes);
      notify(store.getState().dragHandlers.onNodeDrag, event, id, eventNodes(store, drag.moved));
    };

    const handlePointerMove = (event: PointerEvent) => {
      if (drag?.pointerId !== event.pointerId) return;
      // v11 starts auto-panning on the first drag move, so holding a click near the edge does not pan.
      if (!drag.autoPan) drag.autoPan = startAutoPan(store, drag.renderer, moveTo);
      drag.autoPan.update(event);
      moveTo(event);
    };

    const handlePointerEnd = (event: PointerEvent) => {
      if (drag?.pointerId !== event.pointerId) return;
      const { moved, items, hasMoved, autoPan } = drag;
      drag = null;
      autoPan?.stop();
      // Like d3-drag in v11: a drag that moved swallows the click that follows; a plain click stays a click.
      if (hasMoved && element.ownerDocument.defaultView) suppressNextClick(element.ownerDocument.defaultView);
      if (element.hasPointerCapture(event.pointerId)) element.releasePointerCapture(event.pointerId);
      const nodes = eventNodes(store, moved);
      store.getState().triggerNodeChanges(items.map((item) => ({ id: item.id, type: 'position', dragging: false })));
      notify(store.getState().dragHandlers.onNodeDragStop, event, id, nodes);
    };

    element.addEventListener('pointerdown', handlePointerDown);
    element.addEventListener('pointermove', handlePointerMove);
    element.addEventListener('pointerup', handlePointerEnd);
    element.addEventListener('pointercancel', handlePointerEnd);
    return () => {
      drag?.autoPan?.stop();
      element.removeEventListener('pointerdown', handlePointerDown);
      element.removeEventListener('pointermove', handlePointerMove);
      element.removeEventListener('pointerup', handlePointerEnd);
      element.removeEventListener('pointercancel', handlePointerEnd);
    };
  }, [nodeRef, id, isDraggable, store]);
};
